import mqtt from 'mqtt';
import { useEffect, useRef, useState } from 'react';
import type {
  MapMarkerView,
  PolylineView,
} from '@/domains/traffic-monitoring/types/psc-dashboard.types';
import { URLS } from '@/shared/config/urls';

// PSC_MA0302001_BE_004 fan-out: api-dsh publishes every accepted GPS beacon to
// psc/{organization_id}/ambulance/{ambulance_id}/position (retained), so a new
// subscriber immediately receives the LAST known position of every ambulance -
// the map is fully populated on page load, before any fresh beacon arrives.
const POSITION_TOPIC = 'psc/+/ambulance/+/position';

// Flush cadence deliberately matches the beacon interval: positions only
// change every ~5s, and every markers-identity change makes SmartCityMaps
// tear down and rebuild its whole cluster layer (which also collapses any
// hover-expanded cluster). Flushing faster than the data changes would break
// hover every second while buying no extra movement.
const FLUSH_MS = 1000;

// Entries whose recorded_at is older than this are dropped at flush - the same
// window get_map_poi.py uses (stale_after_sec). Without it, the broker's
// retained message would ghost a deleted/silent vehicle on the map forever.
const STALE_MS = 5 * 60 * 1000;

// The dsh broker allows anonymous publish, and SmartCityMaps renders
// tooltip.title via innerHTML. Any markup in broker-sourced strings must die
// HERE, at ingestion, before it can ever reach that sink.
const stripMarkup = (value: string): string => value.replace(/[<>&"'`]/g, '');

export interface AmbulanceLivePosition {
  ambulanceId: number;
  latitude: number;
  longitude: number;
  heading: number | null;
  speedKph: number | null;
  status: string; // raw code, e.g. AMB_ST_ONGO
  policeNumber: string;
  ambulanceReportId: number | null;
  recordedAt: string;
}

export const useAmbulanceLivePositions = (): Map<number, AmbulanceLivePosition> => {
  const [positions, setPositions] = useState<Map<number, AmbulanceLivePosition>>(() => new Map());
  const bufferRef = useRef<Map<number, AmbulanceLivePosition>>(new Map());
  const dirtyRef = useRef(false);

  useEffect(() => {
    const client = mqtt.connect(URLS.MQTT_DSH, { connectTimeout: 5000, keepalive: 15 });

    // Subscribing inside 'connect' re-subscribes after every auto-reconnect;
    // retained delivery then restores the whole fleet without waiting a tick.
    client.on('connect', () => client.subscribe(POSITION_TOPIC));

    client.on('message', (topic, payload) => {
      if (!topic.endsWith('/position') || !topic.includes('/ambulance/')) return;
      let raw: unknown;
      try {
        raw = JSON.parse(payload.toString());
      } catch {
        return; // foreign/broken publisher on the same topic - ignore
      }
      const msg = raw as {
        ambulance_id?: unknown;
        latitude?: unknown;
        longitude?: unknown;
        heading?: unknown;
        speed_kph?: unknown;
        ambulance_status?: unknown;
        police_number?: unknown;
        ambulance_report_id?: unknown;
        recorded_at?: unknown;
      };
      if (
        typeof msg.ambulance_id !== 'number' ||
        typeof msg.latitude !== 'number' ||
        typeof msg.longitude !== 'number'
      ) {
        return;
      }
      bufferRef.current.set(msg.ambulance_id, {
        ambulanceId: msg.ambulance_id,
        latitude: msg.latitude,
        longitude: msg.longitude,
        heading: typeof msg.heading === 'number' ? msg.heading : null,
        speedKph: typeof msg.speed_kph === 'number' ? msg.speed_kph : null,
        status: typeof msg.ambulance_status === 'string' ? stripMarkup(msg.ambulance_status) : '',
        policeNumber: typeof msg.police_number === 'string' ? stripMarkup(msg.police_number) : '',
        ambulanceReportId:
          typeof msg.ambulance_report_id === 'number' ? msg.ambulance_report_id : null,
        recordedAt: typeof msg.recorded_at === 'string' ? msg.recorded_at : '',
      });
      dirtyRef.current = true;
    });

    const flush = setInterval(() => {
      // Staleness pruning runs every tick, not only on fresh messages - a
      // vehicle that stopped beaconing must disappear on its own.
      const now = Date.now();
      for (const [id, entry] of bufferRef.current) {
        const recordedMs = Date.parse(entry.recordedAt);
        if (!Number.isNaN(recordedMs) && now - recordedMs > STALE_MS) {
          bufferRef.current.delete(id);
          dirtyRef.current = true;
        }
      }
      if (!dirtyRef.current) return;
      dirtyRef.current = false;
      setPositions(new Map(bufferRef.current));
    }, FLUSH_MS);

    return () => {
      clearInterval(flush);
      client.end(true);
    };
  }, []);

  return positions;
};

// Overlays live MQTT positions onto the polled POI markers. An ambulance the
// map already shows only gets its COORDINATES moved - the tooltip keeps the
// POI poll's server-localized labels. An ambulance the poll has not caught yet
// is appended, but only when the authenticated roster knows its id: the broker
// accepts anonymous publishes, so an invented ambulance_id must never be able
// to conjure a marker onto the operator's map.
export const overlayLiveAmbulances = (
  markers: MapMarkerView[],
  live: Map<number, AmbulanceLivePosition>,
  knownAmbulanceIds: Set<number>
): MapMarkerView[] => {
  if (live.size === 0) return markers;

  const seen = new Set<number>();
  const merged = markers.map((marker) => {
    if (!marker.id.startsWith('ambulance-')) return marker;
    const ambulanceId = Number(marker.id.slice('ambulance-'.length));
    const pos = live.get(ambulanceId);
    if (!pos) return marker;
    seen.add(ambulanceId);
    return { ...marker, lat: pos.latitude, lng: pos.longitude };
  });

  for (const [ambulanceId, pos] of live) {
    if (seen.has(ambulanceId)) continue;
    if (!knownAmbulanceIds.has(ambulanceId)) continue;
    merged.push({
      id: `ambulance-${ambulanceId}`,
      lat: pos.latitude,
      lng: pos.longitude,
      type: 'ambulancepoi',
      tooltip: {
        title: pos.policeNumber || `Ambulance ${ambulanceId}`,
        description: '',
      },
      isOffline: pos.status === 'AMB_ST_OFF',
    });
  }

  return merged;
};

// Extends each on-going ambulance's route with its latest live position, so
// the polyline grows between the periodic ES-backed refetches instead of
// jumping only every refetchIntervalMs. Only appends to the LAST segment of
// each ambulance's route (matters for a MultiLineString, gap-split trail).
export const overlayLiveRoutePoints = (
  polylines: PolylineView[],
  live: Map<number, AmbulanceLivePosition>
): PolylineView[] => {
  if (live.size === 0 || polylines.length === 0) return polylines;

  const lastSegmentIndex = new Map<number, number>();
  polylines.forEach((line, index) => {
    lastSegmentIndex.set(line.ambulanceId, index);
  });

  return polylines.map((line, index) => {
    if (lastSegmentIndex.get(line.ambulanceId) !== index) return line;
    const pos = live.get(line.ambulanceId);
    if (!pos || pos.status !== 'AMB_ST_ONGO') return line;
    const lastPoint = line.points[line.points.length - 1];
    if (lastPoint && lastPoint[0] === pos.longitude && lastPoint[1] === pos.latitude) return line;
    return { ...line, points: [...line.points, [pos.longitude, pos.latitude]] };
  });
};
